/**
 * balance-ai.ts — runtime resolver for AI tuning (v3M7.1).
 *
 * Replaces the static M9-era TURTLE / RUSH / STEALTH constants with a
 * `getAiConfig(empire, data)` lookup keyed on (race × difficulty ×
 * behaviour). Tuning rows live in `docs/AI_Behaviours.xlsx` and flow
 * through the convert-xlsx pipeline → `src/data/ai-behaviours.json` →
 * `LoadedGameData.aiBehaviours` → `state.aiBehaviours`.
 *
 * Six behaviour archetypes (Expansionist / Economist / Technologist /
 * Fortifier / Aggressive / Balanced) and three difficulty tiers (Easy /
 * Normal / Hard) compose into 18 (race × behaviour) ship-priority rows
 * plus 6 behaviour-shared structure / research / space-structure
 * priority rows. The advance-ai pass reads only the resolved
 * AiRaceConfig — it doesn't need to know whether tunings came from xlsx
 * or hardcode.
 *
 * Patrol radius stays a code-level constant because it's a sim physics
 * knob (movement step), not an AI personality knob.
 */

import type {
  ArchetypeId,
  AiBehavioursData,
  AiDifficulty,
  Empire,
  StructureKind,
} from '../state/types/index';

/**
 * Patrol-ring radius (local 0..1 coords) around the empire's homeworld
 * where idle non-combat ships wander when no targets are visible.
 *
 * v3M10.1: migrated to state.balanceData.ai.patrolRadius.
 */

/**
 * Default difficulty / archetype applied when an empire is created without
 * explicit values (e.g. test fixtures). v4M8.1: 'Normal' renamed to
 * 'Medium' (AiDifficulty union restructured per AI.md §3); 'Balanced'
 * dropped (no equivalent in the new 6-archetype set), so 'Aggressor' is
 * used as the fixture default — closest match to the legacy 'Balanced'
 * tuning (both were `aggressive: true`).
 */
export const DEFAULT_AI_DIFFICULTY: AiDifficulty = 'Medium';
export const DEFAULT_AI_ARCHETYPE: ArchetypeId = 'Aggressor';
/**
 * v4M8.1 — legacy export alias for in-flight call sites that haven't yet
 * migrated from `DEFAULT_AI_BEHAVIOUR`. Resolves to the same value as
 * `DEFAULT_AI_ARCHETYPE`. Remove at v4M8.9 close once all consumers use
 * the new name.
 */
export const DEFAULT_AI_BEHAVIOUR: ArchetypeId = DEFAULT_AI_ARCHETYPE;

/**
 * Tick interval used by the Normal difficulty row, exposed so integration
 * tests can drive the sim forward by `N * DEFAULT_AI_TICK_INTERVAL` ticks
 * without hard-coding a magic number. The xlsx-loaded value remains the
 * runtime source of truth via getAiConfig(); this constant is a fallback /
 * test-side hint.
 *
 * v3M10.1: migrated to state.balanceData.ai.tickInterval.
 */

/**
 * Resolved AI tuning for a single empire on a single tick. Composed at
 * call time from the per-(race × difficulty × behaviour) lookup. The
 * advance-ai pass treats this as the full source of truth — it does not
 * read raceId / difficulty / behaviour directly.
 *
 * Field roles:
 *   - structurePriority — planet-side build order.
 *   - shipPriority — chassis build order at Shipyards.
 *   - researchCategoryPriority — tech-category research order.
 *   - spaceStructurePriority — Constructor-built space-structure order.
 *   - aggressive — combat ships actively close with visible enemies.
 *   - defaultCloak — RACE-CC-only privilege; flipped TRUE only when
 *     race is RACE-CC AND the behaviour row also flags it.
 *   - coloniserPriority (0..10) — drives whether AI builds Colonisers
 *     and how eagerly they're sent to uninhabited worlds (v3M7.3).
 *   - jumpWillingness (0..10) — drives inter-system jump frequency
 *     (v3M7.3).
 *   - retreatHullThreshold (0.0..1.0) — combat ships retreat when
 *     currentHull / maxHull drops below this (v3M7.3).
 *   - tickInterval — ticks between AI decisions (per-empire override).
 *   - startingResourceBonus — applied at scenario init.
 *   - perTickResourceBonus — applied per income tick.
 */
export interface AiRaceConfig {
  structurePriority: readonly StructureKind[];
  shipPriority: readonly string[];
  researchCategoryPriority: readonly string[];
  spaceStructurePriority: readonly string[];
  aggressive: boolean;
  defaultCloak: boolean;
  coloniserPriority: number;
  jumpWillingness: number;
  retreatHullThreshold: number;
  tickInterval: number;
  startingResourceBonus: number;
  perTickResourceBonus: number;
}

/**
 * Resolve the active config for `empire`. Looks up the empire's chosen
 * difficulty + archetype in the loaded `AiBehavioursData` and combines
 * with the race-specific ship priority list. Falls back to the
 * Medium / Aggressor defaults if a row is missing — keeps the sim
 * resilient to xlsx authoring gaps.
 *
 * v4M8.1 — reads `empire.aiArchetype` (renamed from `aiBehaviour`). The
 * underlying `behaviours` map on AiBehavioursData was re-keyed to the new
 * archetype IDs (Aggressor / Defender / Expander / Explorer / Diplomat /
 * Researcher); legacy six (Expansionist / Aggressive / etc.) dropped.
 *
 * Pure — no state mutation, no RNG draws. Safe to call any number of
 * times per tick.
 */
export function getAiConfig(
  empire: Empire,
  data: AiBehavioursData,
): AiRaceConfig {
  const archetype = empire.aiArchetype;
  const difficulty = empire.aiDifficulty;
  const raceId = empire.raceId;

  const behRow =
    data.behaviours[archetype] ?? data.behaviours[DEFAULT_AI_ARCHETYPE];
  const difRow =
    data.difficulties[difficulty] ?? data.difficulties[DEFAULT_AI_DIFFICULTY];

  const structurePriority = (data.structurePriorityByBehaviour[archetype] ??
    data.structurePriorityByBehaviour[DEFAULT_AI_ARCHETYPE] ??
    []) as readonly StructureKind[];
  const shipPriority =
    data.shipPriorityByRaceBehaviour[raceId]?.[archetype] ??
    data.shipPriorityByRaceBehaviour[raceId]?.[DEFAULT_AI_ARCHETYPE] ??
    [];
  const researchCategoryPriority =
    data.researchPriorityByBehaviour[archetype] ??
    data.researchPriorityByBehaviour[DEFAULT_AI_ARCHETYPE] ??
    [];
  const spaceStructurePriority =
    data.spaceStructurePriorityByBehaviour[archetype] ??
    data.spaceStructurePriorityByBehaviour[DEFAULT_AI_ARCHETYPE] ??
    [];

  // Default Cloak is a race-CC-only privilege. Archetype row can flag it
  // ON, but only RACE-CC empires actually pre-cloak their builds.
  const defaultCloak = (behRow?.defaultCloak ?? false) && raceId === 'RACE-CC';

  return {
    structurePriority,
    shipPriority,
    researchCategoryPriority,
    spaceStructurePriority,
    aggressive: behRow?.aggressive ?? false,
    defaultCloak,
    coloniserPriority: behRow?.coloniserPriority ?? 0,
    jumpWillingness: behRow?.jumpWillingness ?? 0,
    retreatHullThreshold: behRow?.retreatHullThreshold ?? 0,
    tickInterval: difRow?.tickInterval ?? 10,
    startingResourceBonus: difRow?.startingResourceBonus ?? 1,
    perTickResourceBonus: difRow?.perTickResourceBonus ?? 1,
  };
}
